<template>
    <div>
        <div class="card border-primary mb-3">
            <h5 class="card-header text-primary">
                <i class="fas fa-user-edit mr-2"></i>
                {{fullName}}
            </h5>

            <div v-if="saving" class="loader"></div>
            <div v-show="!saving" class="card-body">
                <div class="row">
                    <div class="form-group col-sm-12 col-md-4">
                        <label class="font-weight-bold" :class="personEditCardRequiredFields.firstName ? 'required' : ''"
                               :for="'firstName' + _uid">{{$t('person.firstName')}}</label>
                        <input type="text"
                               class="form-control"
                               v-model="model.firstName"
                               v-validate="personEditCardRequiredFields.firstName ? 'required' : ''"
                               :data-vv-as="$t('person.firstName')"
                               :name="'firstName' + _uid"
                               :id="'firstName' + _uid"/>
                        <span class="text-danger" v-if="errors.has('firstName' + _uid)">{{ errors.first('firstName' + _uid) }}</span>
                    </div>

                    <div class="form-group col-sm-12 col-md-4">
                        <label class="font-weight-bold"
                               :class="personEditCardRequiredFields.middleName ? 'required' : ''"
                               :for="'middleName' + _uid">{{ $t('person.middleName') }}</label>
                        <input type="text"
                               class="form-control"
                               v-model="model.middleName"
                               :id="'middleName' + _uid"
                               :name="'middleName' + _uid"
                               :data-vv-as="$t('person.middleName')"
                               v-validate="personEditCardRequiredFields.middleName ? 'required' : ''" />
                        <span class="text-danger" v-if="errors.has('middleName' + _uid)">{{ errors.first('middleName' + _uid) }}</span>
                    </div>

                    <div class="form-group col-sm-12 col-md-4">
                        <label class="font-weight-bold"
                               :class="personEditCardRequiredFields.lastName ? 'required' : ''"
                               :for="'lastName' + _uid">{{$t('person.lastName')}}</label>
                        <input type="text"
                               class="form-control"
                               v-model="model.lastName"
                               v-validate="personEditCardRequiredFields.lastName ? 'required' : ''"
                               :data-vv-as="$t('person.lastName')"
                               :name="'lastName' + _uid"
                               :id="'lastName' + _uid"/>
                        <span class="text-danger" v-if="errors.has('lastName' + _uid)">{{ errors.first('lastName' + _uid) }}</span>
                    </div>

                    <div
                        v-if="showEnNames"
                        class="form-group col-sm-12 col-md-4"
                    >
                        <label class="font-weight-bold"
                               :for="'firstNameEn' + _uid">{{$t('person.firstNameEn')}}</label>
                        <input type="text"
                               class="form-control"
                               v-model="model.firstNameEn"
                               :name="'firstNameEn' + _uid"
                               :id="'firstNameEn' + _uid"/>
                    </div>

                    <div
                        v-if="showEnNames"
                        class="form-group col-sm-12 col-md-4"
                    >
                        <label class="font-weight-bold"
                               :for="'middleNameEn' + _uid">{{$t('person.middleNameEn')}}</label>
                        <input type="text"
                               class="form-control"
                               v-model="model.middleNameEn"
                               :name="'middleNameEn' + _uid"
                               :id="'middleNameEn' + _uid"/>
                    </div>

                    <div
                        v-if="showEnNames"
                        class="form-group col-sm-12 col-md-4"
                    >
                        <label class="font-weight-bold"
                               :for="'lastNameEn' + _uid">{{$t('person.lastNameEn')}}</label>
                        <input type="text"
                               class="form-control"
                               v-model="model.lastNameEn"
                               :name="'lastNameEn' + _uid"
                               :id="'lastNameEn' + _uid"/>
                    </div>
                </div>

                <div class="row">
                    <div class="form-group col-sm-12 col-md-12 col-lg-8">
                        <label class="font-weight-bold" :class="personEditCardRequiredFields.identificationNumber ? 'required' : ''">{{ $t('person.identification') }}</label>
                        <person-identification :identification="model.personIdentification"
                                               :required="personEditCardRequiredFields.identificationNumber"
                                               :disabled="disabledPersonIdentification"
                                               :validateUniqueIdNumber="validateUniqueIdNumber"
                                               :validateEgn="validateEgn"
                                               @getGenderTypeId="model.genderTypeId = $event"></person-identification>
                    </div>

                    <div class="form-group col-sm-12 col-md-6 col-lg-4">
                        <label class="font-weight-bold" :class="personEditCardRequiredFields.gender ? 'required' : ''">{{ $t('person.gender') }}</label>
                        <dropdown :loadByParentSort="true"
                                  :required="personEditCardRequiredFields.gender"
                                  :parentSort="genderTypeSort"
                                  v-model="model.genderTypeId"
                                  :fieldName="$t('person.gender')"
                                  :disabled="false"></dropdown>
                    </div>
                </div>

                <div class="row">
                    <div class="form-group col-sm-12 col-md-4">
                        <label class="font-weight-bold"
                               :class="personEditCardRequiredFields.idCardNumber ? 'required' : ''"
                               :for="'idCardNumber' + _uid">{{ $t('person.idCardNumber') }}</label>
                        <input type="text"
                               class="form-control"
                               v-model="model.idCardNumber"
                               :id="'idCardNumber' + _uid"
                               :name="'idCardDateOfIssue' + _uid"
                               v-validate="personEditCardRequiredFields.idCardNumber ? 'required' : ''"
                               :readonly="false"/>
                        <span class="text-danger" v-if="errors.has('idCardDateOfIssue' + _uid)">{{ errors.first('idCardDateOfIssue' + _uid) }}</span>
                    </div>
                    <div class="form-group col-sm-12 col-md-4">
                        <label class="font-weight-bold"
                               :class="personEditCardRequiredFields.idCardDateOfIssue ? 'required' : ''"
                               :for="'idCardDateOfIssue' + _uid">{{ $t('person.idCardDateOfIssue') }}</label>
                        <date-only-picker ref="idCardDateOfIssue"
                                          v-model="model.idCardDateOfIssue"
                                          :enableTimePicker="false"
                                          :required="personEditCardRequiredFields.idCardDateOfIssue"
                                          :readonly="false"
                                          :showTime="false"
                                          :id="'idCardDateOfIssue'"></date-only-picker>
                    </div>
                    <div class="form-group col-sm-12 col-md-4">
                        <label class="font-weight-bold"
                               :class="personEditCardRequiredFields.idCardIssuedBy ? 'requried' : ''"
                               :for="'idCardIssuedBy' + _uid">{{ $t('person.idCardIssuedBy') }}</label>
                        <input type="text"
                               class="form-control"
                               v-model="model.idCardIssuedBy"
                               :data-vv-as="$t('person.idCardIssuedBy')"
                               v-validate="personEditCardRequiredFields.idCardIssuedBy ? 'requried' : ''"
                               :name="'idCardIssuedBy' + _uid"
                               :id="'idCardIssuedBy' + _uid"
                               :readonly="false"/>
                        <span class="text-danger" v-if="errors.has('idCardIssuedBy' + _uid)">{{ errors.first('idCardIssuedBy' + _uid) }}</span>
                    </div>
                </div>

                <div class="row">
                    <div class="form-group col-sm-12 col-md-4">
                        <label class="font-weight-bold"
                               :class="personEditCardRequiredFields.email ? 'required' : ''"
                               :for="'email' + _uid">{{ $t('email') }}</label>
                        <input type="email"
                               class="form-control"
                               :readonly="isRegistration"
                               v-model="model.email"
                               v-validate="personEditCardRequiredFields.email ? 'required|email' : 'email'"
                               :data-vv-as="$t('email')"
                               :name="'email' + _uid"
                               :id="'email' + _uid"/>
                        <span class="text-danger" v-show="!isRegistration" v-if="errors.has('email' + _uid)">{{ errors.first('email' + _uid) }}</span>
                    </div>

                    <div class="form-group col-sm-12 col-md-4">
                        <label class="font-weight-bold"
                               :class="personEditCardRequiredFields.phone ? 'required' : ''"
                               :for="'phone' + _uid" :readonly="false">{{ $t('person.phone') }}</label>
                        <input type="text"
                               class="form-control"
                               v-model="model.phone"
                               v-validate="personEditCardRequiredFields.phone ? 'required' : ''"
                               :name="'phone' + _uid"
                               :id="'phone' + _uid"
                               :readonly="false"/>
                        <span class="text-danger" v-show="!isRegistration" v-if="errors.has('phone' + _uid)">{{ errors.first('phone' + _uid) }}</span>
                    </div>

                    <div class="form-group col-sm-12 col-md-4">
                        <label class="font-weight-bold"
                               :class="personEditCardRequiredFields.mobilePhone ? 'required' : ''"
                               :for="'mobilePhone' + _uid">{{ $t('person.mobilePhone') }}</label>
                        <input type="text"
                               class="form-control"
                               v-model="model.mobilePhone"
                               v-validate="personEditCardRequiredFields.mobilePhone ? 'required' : ''"
                               :data-vv-as="$t('person.mobilePhone')"
                               :name="'mobilePhone' + _uid"
                               :id="'mobilePhone' + _uid"
                               :readonly="false"/>
                        <span class="text-danger" v-show="!isRegistration" v-if="errors.has('mobilePhone' + _uid)">{{ errors.first('mobilePhone' + _uid) }}</span>
                    </div>
                </div>

                <div class="row">
                    <div class="form-group col-12">
                        <label class="font-weight-bold">{{ $t('person.description') }}</label>
                        <input type="text"
                               class="form-control"
                               v-model="model.description"
                               :id="'description' + _uid"
                               :readonly="false"/>
                    </div>
                </div>

                <InstitutionSelector v-model="model" :is-required="personEditCardRequiredFields.institution"></InstitutionSelector>

                <div class="row">
                    <div class="form-group col-sm-12 col-md-4">
                        <label class="font-weight-bold" :class="positionStatIsRequired ? 'required' : ''">{{$t('person.positionStatistic') }}</label>
                        <dropdown :loadByParentSort="true"
                                  :parentSort="positionStatisticSort"
                                  :required="positionStatIsRequired"
                                  v-model="model.positionStatisticId"
                                  :fieldName="$t('person.positionStatistic')"
                                  :disabled="false">
                        </dropdown>
                    </div>

                    <div class="form-group col-sm-12 col-md-4">
                        <label class="font-weight-bold" :class="positionIsRequired ? 'required' : ''">{{ $t('person.position') }}</label>
                        <dropdown :loadByParentSort="true"
                                  :parentSort="positionSort"
                                  :required="positionIsRequired"
                                  v-model="model.position"
                                  :fieldName="$t('person.position')"
                                  :disabled="false">
                        </dropdown>
                    </div>

                    <div class="form-group col-sm-12 col-md-4">
                        <label class="font-weight-bold" :for="'positionStr' + _uid">{{ $t('person.positionFullName') }}</label>
                        <input type="text" class="form-control" v-model="model.positionStr" :id="'positionStr' + _uid"/>
                    </div>
                </div>

                <div class="row">
                    <div class="form-group col-sm-12 col-md-4">
                        <label class="font-weight-bold">{{ $t('person.salutation') }}</label>
                        <dropdown :loadByParentSort="true" :parentSort="salutationSort" v-model="model.salutationId"
                                  :fieldName="$t('person.salutation')" :disabled="false"></dropdown>
                    </div>

                    <div class="form-group col-sm-12 col-md-4">
                        <label class="font-weight-bold">{{ $t('person.titleDegree') }}</label>
                        <dropdown :loadByParentSort="true" :parentSort="titleDegreeSort" v-model="model.titleDegreeId"
                                  :fieldName="$t('person.titleDegree')" :disabled="false"></dropdown>
                    </div>

                    <div class="form-group col-sm-12 col-md-4">
                        <label class="font-weight-bold" :class="personEditCardRequiredFields.education ? 'required' : ''">{{ $t('person.educationDegree') }}</label>
                        <dropdown :loadByParentSort="true"
                                  :parentSort="educationDegreeSort"
                                  :required="personEditCardRequiredFields.education"
                                  v-model="model.educationDegreeId"
                                  :fieldName="$t('person.educationDegree')"
                                  :disabled="false">
                        </dropdown>
                    </div>
                </div>

                <div>
                    <div class="alert alert-info"><span class="fas fa-info-circle"></span> {{ $t("person.livingAddress") }}</div>
                    <address-component :address="model.address"
                                       :required="false">
                    </address-component>
                </div>

                <div>
                    <bankaccount-component :bank-account="model.bankAccount"
                                           :required="false">
                    </bankaccount-component>
                </div>

                <div v-if="!hideBtns" class="text-right mt-3">
                    <div class="btn-group" role="group" aria-label="Person edit buttons">
                        <button type="button" class="btn btn-success" @click.stop="onSave">
                            <i class="fas fa-save mr-2"></i>
                            {{ $t('buttons.save.btnText')}}
                        </button>
                        <button type="button" class="btn btn-danger" @click.stop="onCancel">
                            <i class="fas fa-ban mr-2"></i>
                            {{ $t('buttons.cancel')}}
                        </button>
                    </div>
                </div>
            </div>

            <div class="card-footer text-muted" v-if="model.relatedUsername">{{ $t('person.relatedToUser') + '"' +
                model.relatedUsername + '"' }}
            </div>
        </div>
    </div>
</template>

<script>
    import { Person } from '@/models/models';
    import PersonIdentificationComponent from '../person/PersonIdentification';
    import InstitutionSelector from '../../components/common/InstitutionSelector';
    import Dropdown from '../dropdown/NomenclatureDropdown';
    import { Culture, NomenclatureParentBySort } from '@/models/enums';
    import DateOnlyPicker from '../../components/datepicker/DateOnlyPicker';
    import personService from '../../services/person.service';
    import Address from "../address/Address";
    import BankAccount from "../bankAccount/BankAccount";
    import { mapGetters } from "vuex";

    export default {
        inject: ['$validator'],
        props: {
            value: {
                type: Person,
                required: false
            },
            create: {
                type: Boolean,
                default: false
            },
            validateUniqueIdNumber: {
                type: Boolean,
                default: false
            },
            validateEgn: {
                type: Boolean,
                default: false
            },
            hideBtns: {
                type: Boolean,
                default: false
            },
            isRegistration: {
                type: Boolean,
                default: false
            },
            positionStatIsRequired: {
                type: Boolean,
                default: true
            },
            positionIsRequired: {
                type: Boolean,
                default: true
            },
            showEnNames: {
                type: Boolean,
                default: false
            },
            disabledPersonIdentification: {
                type: Boolean,
                default: false
            }
        },
        components: {
            Dropdown,
            'person-identification': PersonIdentificationComponent,
            DateOnlyPicker,
            InstitutionSelector,
            'address-component': Address,
            'bankaccount-component': BankAccount
            // PersonIdentificationDetails
        },
        data() {
            return {
                saving: false,
                model: this.value,
                genderTypeSort: NomenclatureParentBySort.GenderType,
                positionStatisticSort: NomenclatureParentBySort.PositionStatistic,
                positionSort: NomenclatureParentBySort.Position,
                salutationSort: NomenclatureParentBySort.Salutation,
                titleDegreeSort: NomenclatureParentBySort.TitleDegree,
                educationDegreeSort: NomenclatureParentBySort.EducationDegree
            };
        },
        methods: {
            odata4ParameterMap: function (options, operation) {
                if (operation !== 'read' && options.models) {
                    return { models: kendo.stringify(options.models) };
                } else {
                    const newOptions = kendo.data.transports['odata-v4'].parameterMap(options, operation);

                    return newOptions;
                }
            },
            beforeSend: function (req) {
                if (this.token) {
                    req.setRequestHeader('Authorization', 'Bearer ' + this.token);
                }
            },
            onCancel() {
                this.$emit('cancel');
            },
            onSave() {
                this.$validator.validateAll().then(result => {
                    if (result) {
                        kendo
                            .confirm(`${this.create ? this.$t('person.confirmations.create') : this.$t('person.confirmations.edit')} '${this.fullName}'?`)
                            .then(
                                function () {
                                    this.doSave();
                                }.bind(this)
                            );
                    }
                });
            },
            doSave() {
                if (this.create && this.isNew) {
                    // Create
                    if (this.model.profileManagement) {
                        personService
                            .checkUserToPersonEmailMatch(this.model)
                            .then(response => {
                                if (response.data && response.data.match) {
                                    this.createPerson();
                                } else {
                                    this.$vs.notify({
                                        title: '',
                                        text: this.$helpers.responseMessage(response),
                                        color: 'warning',
                                        time: this.$store.getters.notificationDuration
                                    });
                                }
                            })
                            .catch(error => {
                                this.$vs.notify({
                                    title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                                    text: this.$helpers.errorText(error),
                                    color: this.$helpers.errorColor(error),
                                    time: this.$store.getters.notificationDuration
                                });
                            });
                    } else {
                        this.createPerson();
                    }
                } else {
                    // Update
                    if (this.model.profileManagement) {
                        personService
                            .checkUserToPersonEmailMatch(this.model)
                            .then(response => {
                                if (response.data && response.data.match) {
                                    this.updatePerson();
                                } else {
                                    this.$vs.notify({
                                        title: '',
                                        text: this.$helpers.responseMessage(response),
                                        color: 'warning',
                                        time: this.$store.getters.notificationDuration
                                    });
                                }
                            })
                            .catch(error => {
                                this.$vs.notify({
                                    title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                                    text: this.$helpers.errorText(error),
                                    color: this.$helpers.errorColor(error),
                                    time: this.$store.getters.notificationDuration
                                });
                            });
                    } else {
                        this.updatePerson();
                    }
                }
            },
            createPerson() {
                this.saving = true;

                personService
                    .create(this.model)
                    .then(response => {
                        let resultModel = response.data.model;

                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.responseStatusCode(response)),
                            text: this.$helpers.responseMessage(response),
                            color: 'success',
                            time: this.$store.getters.notificationDuration
                        });

                        this.$emit('save', resultModel);
                    })
                    .catch(error => {
                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: this.$helpers.errorColor(error),
                            time: this.$store.getters.notificationDuration
                        });
                    })
                    .then(() => { this.saving = false; });
            },
            updatePerson() {
                this.saving = true;

                personService
                    .update(this.model)
                    .then(response => {
                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.responseStatusCode(response)),
                            text: this.$helpers.responseMessage(response),
                            color: 'success',
                            time: this.$store.getters.notificationDuration
                        });

                        this.$emit('save', this.model);
                    })
                    .catch(error => {
                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: this.$helpers.errorColor(error),
                            time: this.$store.getters.notificationDuration
                        });
                    })
                    .then(() => { this.saving = false; });
            }
        },
        computed: {
            token: function () {
                return this.$store.getters.authToken;
            },
            isEnVersion() {
                return this.$store.getters.currentCulture === Culture.EN;
            },
            fullName() {
                let name = this.model.firstName;
                if (this.model.middleName) {
                    name += ` ${this.model.middleName}`;
                }

                if (this.model.lastName) {
                    name += ` ${this.model.lastName}`;
                }

                return name;
            },
            isNew() {
                let id = Number(this.model.id);
                return isNaN(id) || id <= 0;
            },
            areaOfAppeal() {
                if (!this.model.areaOfAppeal) {
                    return '';
                }

                return `${
                    this.isEnVersion
                        ? this.model.areaOfAppeal.nameEn
                        : this.model.areaOfAppeal.name
                } \ ${this.model.areaOfAppeal.districtCode}`;
            },
            ...mapGetters(['personEditCardRequiredFields'])
        },
        watch: {
            value: function (val) {
                if (val) {
                    this.model = new Person(this.value);
                }
            }
        }
    };
</script>

<style scoped>
    input[readonly] {
        background-color: whitesmoke;
    }

    textarea[readonly] {
        background-color: whitesmoke;
    }
</style>
